import {
  UsageBusinessAdoptionRow,
  UsageDailyAggregate,
} from '../types/usageTracking';

export function mapUsageDailyDoc(
  id: string,
  data: Record<string, unknown>
): UsageDailyAggregate {
  const lastEventAtRaw = data.lastEventAt as { toDate?: () => Date } | Date | null | undefined;
  let lastEventAt: Date | null = null;
  if (lastEventAtRaw && typeof (lastEventAtRaw as { toDate?: () => Date }).toDate === 'function') {
    lastEventAt = (lastEventAtRaw as { toDate: () => Date }).toDate();
  } else if (lastEventAtRaw instanceof Date) {
    lastEventAt = lastEventAtRaw;
  }

  return {
    id,
    businessId: String(data.businessId || ''),
    date: String(data.date || ''),
    logins: Number(data.logins || 0),
    sessionStarts: Number(data.sessionStarts || 0),
    pageViews: Number(data.pageViews || 0),
    posSales: Number(data.posSales || 0),
    clientsCreated: Number(data.clientsCreated || 0),
    appointmentsCreated: Number(data.appointmentsCreated || 0),
    stockMovements: Number(data.stockMovements || 0),
    modules: (data.modules as Record<string, number>) || {},
    lastEventAt,
    lastUserId: data.lastUserId ? String(data.lastUserId) : undefined,
    lastEvent: data.lastEvent ? String(data.lastEvent) : undefined,
  };
}

export function buildAdoptionRows(
  dailies: UsageDailyAggregate[],
  businesses: Array<{ id: string; name: string }>
): UsageBusinessAdoptionRow[] {
  const byBusiness = new Map<
    string,
    {
      logins: number;
      posSales: number;
      clientsCreated: number;
      appointmentsCreated: number;
      stockMovements: number;
      pageViews: number;
      activeDays: Set<string>;
      lastEventAt: Date | null;
      modules: Record<string, number>;
    }
  >();

  dailies.forEach((d) => {
    if (!d.businessId) return;
    let row = byBusiness.get(d.businessId);
    if (!row) {
      row = {
        logins: 0,
        posSales: 0,
        clientsCreated: 0,
        appointmentsCreated: 0,
        stockMovements: 0,
        pageViews: 0,
        activeDays: new Set(),
        lastEventAt: null,
        modules: {},
      };
      byBusiness.set(d.businessId, row);
    }
    row.logins += d.logins;
    row.posSales += d.posSales;
    row.clientsCreated += d.clientsCreated;
    row.appointmentsCreated += d.appointmentsCreated;
    row.stockMovements += d.stockMovements;
    row.pageViews += d.pageViews;
    if (
      d.logins +
        d.posSales +
        d.clientsCreated +
        d.appointmentsCreated +
        d.stockMovements +
        d.pageViews +
        d.sessionStarts >
      0
    ) {
      row.activeDays.add(d.date);
    }
    if (d.lastEventAt && (!row.lastEventAt || d.lastEventAt > row.lastEventAt)) {
      row.lastEventAt = d.lastEventAt;
    }
    Object.entries(d.modules || {}).forEach(([mod, count]) => {
      row!.modules[mod] = (row!.modules[mod] || 0) + Number(count || 0);
    });
  });

  const nameById = new Map(businesses.map((b) => [b.id, b.name]));

  const rows: UsageBusinessAdoptionRow[] = [];
  byBusiness.forEach((agg, businessId) => {
    const topModules = Object.entries(agg.modules)
      .map(([module, count]) => ({ module, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    rows.push({
      businessId,
      businessName: nameById.get(businessId) || businessId,
      logins: agg.logins,
      posSales: agg.posSales,
      clientsCreated: agg.clientsCreated,
      appointmentsCreated: agg.appointmentsCreated,
      stockMovements: agg.stockMovements,
      pageViews: agg.pageViews,
      activeDays: agg.activeDays.size,
      lastEventAt: agg.lastEventAt,
      topModules,
    });
  });

  // Inclure aussi les businesses sans activité (0)
  businesses.forEach((b) => {
    if (!byBusiness.has(b.id)) {
      rows.push({
        businessId: b.id,
        businessName: b.name,
        logins: 0,
        posSales: 0,
        clientsCreated: 0,
        appointmentsCreated: 0,
        stockMovements: 0,
        pageViews: 0,
        activeDays: 0,
        lastEventAt: null,
        topModules: [],
      });
    }
  });

  return rows.sort((a, b) => {
    const ta = a.lastEventAt?.getTime() ?? 0;
    const tb = b.lastEventAt?.getTime() ?? 0;
    if (tb !== ta) return tb - ta;
    return a.businessName.localeCompare(b.businessName, 'fr');
  });
}
